import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, USER_ROLES } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  FileText, MoreHorizontal, UserPlus, CheckCircle2, XCircle, 
  Clock, AlertCircle, Search, Filter, ArrowUpRight,
  ClipboardList, UserCheck, Gavel, MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const statusConfig = {
  draft: { label: "Draft", color: "bg-slate-500/20 text-slate-400 border-slate-500/30", icon: FileText },
  submitted: { label: "Submitted", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Clock },
  under_review: { label: "Under Review", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: ClipboardList },
  decision_pending: { label: "Decision Pending", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Gavel },
  approved: { label: "Approved", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  denied: { label: "Denied", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
  more_info_required: { label: "More Info Required", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: MessageSquare }
};

const urgencyConfig = {
  routine: { label: "Routine", color: "text-slate-400" },
  urgent: { label: "Urgent", color: "text-amber-400" },
  stat: { label: "STAT", color: "text-red-400" }
};

export default function ReviewQueue() {
  const navigate = useNavigate();
  const { user, organization, isAdmin } = useAuth();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [assignedToMe, setAssignedToMe] = useState(false);
  
  // Users for assignment
  const [orgUsers, setOrgUsers] = useState([]);
  
  // Assignment dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [assignTo, setAssignTo] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assigning, setAssigning] = useState(false);
  
  // Status update dialog
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [denialReason, setDenialReason] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchQueue();
    fetchOrgUsers();
  }, [statusFilter, assignedToMe]);

  const fetchQueue = async () => {
    try {
      let url = `${API}/cases/queue?limit=100`;
      if (statusFilter && statusFilter !== "all") url += `&status=${statusFilter}`;
      if (assignedToMe) url += `&assigned_to_me=true`;
      
      const response = await axios.get(url, { withCredentials: true });
      setCases(response.data.cases || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching queue:", error);
      if (error.response?.status !== 403) {
        toast.error("Failed to load review queue");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgUsers = async () => {
    try {
      const response = await axios.get(`${API}/org/users`, { withCredentials: true });
      // Filter to only reviewers, decision makers, and admins
      const eligibleUsers = response.data.filter(u => 
        ["reviewer", "decision_maker", "admin"].includes(u.role) && 
        u.status === "active"
      );
      setOrgUsers(eligibleUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleOpenAssign = (caseItem) => {
    setSelectedCase(caseItem);
    setAssignTo(caseItem.assigned_to || "");
    setAssignNotes("");
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    if (!assignTo) {
      toast.error("Please select a user to assign");
      return;
    }

    setAssigning(true);
    try {
      await axios.post(
        `${API}/cases/${selectedCase.pa_id}/assign`,
        { assigned_to: assignTo, notes: assignNotes },
        { withCredentials: true }
      );
      toast.success("Case assigned successfully");
      setAssignOpen(false);
      fetchQueue();
    } catch (error) {
      console.error("Assign error:", error);
      toast.error(error.response?.data?.detail || "Failed to assign case");
    } finally {
      setAssigning(false);
    }
  };

  const handleOpenStatusUpdate = (caseItem) => {
    setSelectedCase(caseItem);
    setNewStatus("");
    setStatusNotes("");
    setDenialReason("");
    setStatusOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      toast.error("Please select a status");
      return;
    }

    if (newStatus === "denied" && !denialReason) {
      toast.error("Please provide a denial reason");
      return;
    }

    setUpdatingStatus(true);
    try {
      await axios.post(
        `${API}/cases/${selectedCase.pa_id}/status`,
        { 
          status: newStatus, 
          notes: statusNotes,
          denial_reason: newStatus === "denied" ? denialReason : undefined
        },
        { withCredentials: true }
      );
      toast.success("Status updated successfully");
      setStatusOpen(false);
      fetchQueue();
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filteredCases = cases.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.patient_name?.toLowerCase().includes(search) ||
      c.pa_id?.toLowerCase().includes(search) ||
      c.service_type?.toLowerCase().includes(search)
    );
  });

  const getAvailableStatuses = (currentStatus) => {
    const transitions = {
      submitted: ["under_review"],
      under_review: ["decision_pending", "more_info_required"],
      more_info_required: ["under_review", "decision_pending"],
      decision_pending: ["approved", "denied", "under_review"]
    };
    return transitions[currentStatus] || [];
  };

  const isDecisionMaker = user?.role === "decision_maker" || user?.role === "admin";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="review-queue-title">
              Review Queue
            </h1>
            <p className="text-slate-400 mt-1">
              Manage and process prior authorization requests
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.submitted || 0}</p>
                  <p className="text-sm text-slate-400">Submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <ClipboardList className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.under_review || 0}</p>
                  <p className="text-sm text-slate-400">Under Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Gavel className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.decision_pending || 0}</p>
                  <p className="text-sm text-slate-400">Pending Decision</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.approved || 0}</p>
                  <p className="text-sm text-slate-400">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="search-cases"
                    placeholder="Search by patient, PA ID, or service..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger 
                  data-testid="status-filter"
                  className="w-[180px] bg-slate-900/50 border-slate-600 text-white"
                >
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-slate-300">All Statuses</SelectItem>
                  {Object.entries(statusConfig).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} className="text-slate-300">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant={assignedToMe ? "default" : "outline"}
                onClick={() => setAssignedToMe(!assignedToMe)}
                className={assignedToMe 
                  ? "bg-emerald-600 hover:bg-emerald-700" 
                  : "border-slate-600 text-slate-300 hover:bg-slate-700"
                }
                data-testid="my-cases-filter"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                My Cases
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cases Table */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Cases ({filteredCases.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-slate-400">Loading cases...</p>
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-lg">No cases found</p>
                <p className="text-slate-500 text-sm mt-1">
                  {assignedToMe ? "No cases are assigned to you" : "Try adjusting your filters"}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">PA ID</TableHead>
                    <TableHead className="text-slate-400">Patient</TableHead>
                    <TableHead className="text-slate-400">Submitted By</TableHead>
                    <TableHead className="text-slate-400">Service</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Urgency</TableHead>
                    <TableHead className="text-slate-400">Assigned To</TableHead>
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCases.map((c) => {
                    const statusInfo = statusConfig[c.status] || statusConfig.draft;
                    const StatusIcon = statusInfo.icon;
                    const urgencyInfo = urgencyConfig[c.urgency] || urgencyConfig.routine;
                    
                    return (
                      <TableRow 
                        key={c.pa_id} 
                        className="border-slate-700 hover:bg-slate-700/30"
                        data-testid={`case-row-${c.pa_id}`}
                      >
                        <TableCell className="font-mono text-sm text-slate-300">
                          {c.pa_id}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{c.patient_name || "Unknown"}</p>
                            <p className="text-slate-400 text-xs">{c.patient_insurance}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {c.submitting_org_name ? (
                            <div>
                              <p className="text-emerald-400 font-medium text-sm">{c.submitting_org_name}</p>
                              <p className="text-slate-500 text-xs capitalize">{c.submitting_org_type || 'provider'}</p>
                            </div>
                          ) : (
                            <span className="text-slate-500 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-300">{c.service_type}</TableCell>
                        <TableCell>
                          <Badge className={`${statusInfo.color} border`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={`font-medium ${urgencyInfo.color}`}>
                            {urgencyInfo.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          {c.assignee_name ? (
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                <span className="text-xs text-emerald-400">
                                  {c.assignee_name.charAt(0)}
                                </span>
                              </div>
                              <span className="text-slate-300 text-sm">{c.assignee_name}</span>
                            </div>
                          ) : (
                            <span className="text-slate-500 text-sm">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-slate-400 hover:text-white hover:bg-slate-700"
                                data-testid={`case-actions-${c.pa_id}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent 
                              align="end"
                              className="bg-slate-800 border-slate-700"
                            >
                              <DropdownMenuItem 
                                onClick={() => navigate(`/pa-requests/${c.pa_id}/workflow`)}
                                className="text-slate-300 focus:bg-slate-700 focus:text-white"
                              >
                                <ArrowUpRight className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-slate-700" />
                              <DropdownMenuItem 
                                onClick={() => handleOpenAssign(c)}
                                className="text-slate-300 focus:bg-slate-700 focus:text-white"
                              >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Assign
                              </DropdownMenuItem>
                              {getAvailableStatuses(c.status).length > 0 && (
                                <DropdownMenuItem 
                                  onClick={() => handleOpenStatusUpdate(c)}
                                  className="text-slate-300 focus:bg-slate-700 focus:text-white"
                                >
                                  <ClipboardList className="w-4 h-4 mr-2" />
                                  Update Status
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Assign Dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Assign Case</DialogTitle>
              <DialogDescription className="text-slate-400">
                Assign PA {selectedCase?.pa_id} to a team member
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Assign To</Label>
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {orgUsers.map((u) => (
                      <SelectItem 
                        key={u.user_id} 
                        value={u.user_id}
                        className="text-slate-300"
                      >
                        {u.name} ({USER_ROLES[u.role]?.label || u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Notes (optional)</Label>
                <Textarea
                  value={assignNotes}
                  onChange={(e) => setAssignNotes(e.target.value)}
                  placeholder="Add any notes for the assignee..."
                  className="bg-slate-900/50 border-slate-600 text-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setAssignOpen(false)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssign}
                disabled={assigning || !assignTo}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {assigning ? "Assigning..." : "Assign Case"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Update Dialog */}
        <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Update Case Status</DialogTitle>
              <DialogDescription className="text-slate-400">
                Update status for PA {selectedCase?.pa_id}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {selectedCase && getAvailableStatuses(selectedCase.status).map((status) => {
                      const info = statusConfig[status];
                      // Only show decision statuses to decision makers
                      if ((status === "approved" || status === "denied") && !isDecisionMaker) {
                        return null;
                      }
                      return (
                        <SelectItem 
                          key={status} 
                          value={status}
                          className="text-slate-300"
                        >
                          {info?.label || status}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {newStatus === "denied" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Denial Reason *</Label>
                  <Textarea
                    value={denialReason}
                    onChange={(e) => setDenialReason(e.target.value)}
                    placeholder="Please provide the reason for denial..."
                    className="bg-slate-900/50 border-slate-600 text-white"
                    required
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-300">Notes (optional)</Label>
                <Textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add any notes about this status change..."
                  className="bg-slate-900/50 border-slate-600 text-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setStatusOpen(false)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate}
                disabled={updatingStatus || !newStatus || (newStatus === "denied" && !denialReason)}
                className={newStatus === "approved" 
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : newStatus === "denied"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-blue-600 hover:bg-blue-700"
                }
              >
                {updatingStatus ? "Updating..." : "Update Status"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
